import {
  hasVapidKey,
  pushNote,
  pushPromptOf,
  signedInCustomerId,
  useCustomerAuth,
  usePush,
} from '@agos/customer-core'
import { T, tt, useModalFocus } from '@agos/shared'
import { Bell, Smartphone, X } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'

import { usePageDialogs } from './other-dialogs'

const PRIMARY_BUTTON =
  'min-h-[48px] w-full rounded-2xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-60 transition-colors'
const QUIET_BUTTON =
  'min-h-[48px] w-full rounded-2xl border border-border bg-background text-foreground font-semibold text-sm hover:bg-muted disabled:opacity-60 transition-colors'

/**
 * The offer to switch push notifications on - a popup, on every page, until the customer says *Nie*.
 * `FRONTEND.md` §9.737, owner O107, FB-1043.
 *
 * The owner: *"make sure popup for allowing notifications will show always when logged in and they not used "No",
 * and make it available everywhere and make it as POPUP - not at the bottom of the webpage."* What it replaces was a
 * plain card with no positioning, mounted after the footer, so it was drawn in the page's flow under the last thing a
 * customer scrolls to; and its *Teraz nie* lasted one page view, so a reload asked again and no answer stopped it.
 *
 * **A modal dialog**, centred over a dimmed page, named by its own heading, with the focus confined by
 * `useModalFocus` - so `EveryModalOverlayConfinesFocusTest` counts it like every other dialog. **When it opens** is
 * `pushPromptOf`, a pure rule with a case per condition; this component only gathers the facts and draws the answer.
 * It renders above the router (the shell mounts it with the other app-wide surfaces), so it asks on every route.
 *
 * **Three answers** (point 3):
 * - *Zapnúť upozornenia* is `usePush().enable()`, unchanged. On success the state is `granted-active` and the rule
 *   closes the dialog; a failure shows its own sentence here, and the dialog stays open.
 * - *Nie, ďakujem* is remembered on this device for this account until push is switched on again.
 * - *Zavrieť* - the X, Escape, the backdrop - is for now: not again in this tab for this visit (two hours at most),
 *   asked again on the next. **The X is the cancel target the opening focus lands on**, so a reflexive Enter neither
 *   subscribes nor says *Nie* for ever. Nothing closes while a subscribe is in flight, because the answer to it
 *   belongs in this dialog.
 *
 * **An iPhone or iPad tab gets the account row's own instruction** (point 5, owner question #149): nothing on a
 * Safari page can subscribe it, and the same phone can from the home screen. The sentence is `pushNote`'s rather than
 * a second spelling of it.
 *
 * **It does not wait for the cookie question, and it does wait for any other dialog** - both corrected after review.
 * The cookie card is not modal (its own docstring says so), so waiting for it meant a customer who never answered it
 * was never asked; the offer's backdrop sits above the card's, which waits dimmed behind it. Another dialog is a
 * different matter: the offer opens when the browser and the server answer, and opening then underneath the lunch
 * menu or the wheel would put the keyboard's focus on a button nobody can see. And it steps aside for a dialog that
 * opens after it - the gift offer and the add-on prompt open on their own schedule too, below it - without taking
 * that dialog's focus, and comes back when it closes (`other-dialogs.ts`).
 */
export function PushOptInDialog() {
  const push = usePush()
  // The same selector `usePush` keys the remembered answers by, so the two cannot disagree about who is asked.
  const customerId = useCustomerAuth(signedInCustomerId)
  const facts = {
    state: push.state,
    signedIn: customerId !== null,
    keyConfigured: hasVapidKey(push.publicKey),
    settled: push.settled,
    declined: push.declined,
    closedThisTab: push.closedThisTab,
  }
  /* Two answers from one rule (corrected after review). `wanted` is every fact but the page - and only while it holds
   * is the page watched, so a visitor, or a customer who has answered, costs nothing. `prompt` adds the page: no
   * other dialog is up, so the offer is on screen. */
  const wanted = pushPromptOf({ ...facts, anotherDialogOpen: false })
  const page = usePageDialogs(wanted !== null)
  const prompt = pushPromptOf({ ...facts, anotherDialogOpen: page.anotherDialogOpen })
  /* **Mounted once it has been shown, and kept mounted - hidden - while it steps aside.** Waiting for a dialog that
   * was up first, it is not mounted, so it takes no focus it could not show. Stepping aside for one that came after
   * it, it stays mounted, so `useModalFocus` does not close and hand the focus back to the page from under that
   * dialog. */
  const mounted = prompt !== null || (wanted !== null && page.offerOnPage)

  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const headingId = useId()
  const bodyId = useId()
  /* **Dismissible even mid-subscribe** (the lead, after the review). The subscribe waits on the browser's own
   * permission prompt, and Chrome's quieter prompt is a chip in the address bar a customer may never notice - so a
   * popup that could not be closed while it waited would hold the whole shop behind it. Closing is *for now*, as
   * always; the subscribe carries on, and its outcome is on the account's own push row. *Nie* stays locked while
   * it runs, because saying no to what is being switched on is not an answer the dialog can keep. */
  const close = push.closeForNow
  const { onKeyDown } = useModalFocus(mounted, panelRef, close)
  /* Shown again after stepping aside, it takes the focus back: the hook focused it once, when it opened, and the
   * dialog it stepped aside for has handed its own focus back to the page behind this one's backdrop. */
  useEffect(() => {
    if (prompt !== null) closeRef.current?.focus()
  }, [prompt])

  if (!mounted || wanted === null) return null

  const homeScreen = wanted === 'home-screen'

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-push-backdrop
      // Stepped aside for another dialog: `[hidden]` is `display: none` in this app's preflight, whatever the classes.
      hidden={prompt === null}
      onClick={close ?? undefined}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={bodyId}
        data-push-prompt={push.state}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-w-sm max-h-full overflow-y-auto rounded-3xl bg-card border border-border shadow-2xl px-5 pt-8 pb-5 text-center space-y-4"
      >
        <button
          ref={closeRef}
          type="button"
          data-push-close
          data-dialog-cancel
          onClick={push.closeForNow}
          aria-label={tt('Zavrieť')}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center">
          {homeScreen ? <Smartphone className="w-7 h-7" /> : <Bell className="w-7 h-7" />}
        </div>

        <h2 id={headingId} className="text-lg font-bold text-foreground">
          {homeScreen ? <T>Upozornenia na iPhone a iPade</T> : <T>Zapnúť upozornenia?</T>}
        </h2>
        <p id={bodyId} className="text-sm text-muted-foreground leading-relaxed" data-push-body>
          {homeScreen ? (
            pushNote('home-screen')
          ) : (
            <T>Pošleme vám upozornenie pri akciách, špeciálnych ponukách a zmene stavu objednávky.</T>
          )}
        </p>

        {push.problem !== '' && (
          <p className="text-xs text-red-500" role="alert" data-push-problem>
            {push.problem}
          </p>
        )}

        <div className="flex flex-col gap-2 pt-1">
          {homeScreen ? (
            <button type="button" data-push-understood onClick={push.closeForNow} className={PRIMARY_BUTTON}>
              <T>Rozumiem</T>
            </button>
          ) : (
            <button
              type="button"
              data-push-enable
              disabled={push.busy}
              onClick={() => void push.enable()}
              className={PRIMARY_BUTTON}
            >
              {push.busy ? <T>Zapínam…</T> : <T>Zapnúť upozornenia</T>}
            </button>
          )}
          <button
            type="button"
            data-push-decline
            disabled={push.busy}
            onClick={push.decline}
            className={QUIET_BUTTON}
          >
            <T>Nie, ďakujem</T>
          </button>
        </div>

        {!homeScreen && (
          <p className="text-[11px] text-muted-foreground">
            <T>Kedykoľvek ich zapnete alebo vypnete v profile.</T>
          </p>
        )}
      </div>
    </div>
  )
}
